import React, {
  useState,
  useEffect,
  memo,
  lazy,
  Suspense,
  useMemo,
} from "react";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  styled,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Grid,
  Button,
  Skeleton,
  Pagination,
  Chip,
  Dialog,
  CircularProgress,
  Accordion,
  LinearProgress,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  VisibilityOff,
  Visibility,
  LocalLibrary as ReadIcon,
  Add as AddIcon,
  MenuBook as MenuBookIcon,
  LocalAtm as LocalAtmIcon,
  Publish as PublishIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  PictureAsPdf as PdfIcon,
  Headphones as AudioIcon,
  PlayArrow as PlayIcon,
  Description,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Autorenew as AutorenewIcon,
  HourglassEmpty as PendingIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import useUser from "../../../hooks/api-request/useUser";
import useUpdate from "../../../api/useUpdate";
import SnackbarMessage from "../../../components/common/SnackbarMessage";
import { format as formatDate } from "date-fns";
import useUserPurchases from "../../../hooks/api-request/useUserPurchases";
import useAuthoredBooks from "../../../hooks/api-request/useAuthoredBooks";
import BookTableSkeleton from "../../../components/skeletons/BookTableSkeleton";
import { throttle } from "lodash";
import useSubscriptions from "../../../hooks/api-request/useSubscriptions";
import useMagazinePurchases from "../../../hooks/api-request/useMagazinePurchases";

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
  "& .MuiTab-root": {
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 500,
    minWidth: "auto",
    padding: theme.spacing(2, 3),
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
}));

const InfoSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(4),
}));

const InfoGrid = styled(Grid)(({ theme }) => ({
  "& .MuiGrid-item": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
}));

const InfoSectionWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  "&:hover .edit-icon": {
    opacity: 1,
  },
}));

const EditIconWrapper = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  opacity: 0,
  transition: "opacity 0.2s ease-in-out",
  cursor: "pointer",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const ProfileSkeleton = memo(() => (
  <Box sx={{ width: "100%" }}>
    <InfoSection>
      <Grid container spacing={4}>
        {[1, 2, 3].map((item) => (
          <Grid item xs={12} sm={4} key={item}>
            <InfoLabel>
              <Skeleton width={80} />
            </InfoLabel>
            <InfoValue>
              <Skeleton width={150} />
            </InfoValue>
          </Grid>
        ))}
      </Grid>
    </InfoSection>
  </Box>
));

// Password requirements are defined here, but the actual display text comes from translations
const PASSWORD_REQUIREMENTS = [
  { rule: (v) => v.length >= 8, message: "length" },
  { rule: (v) => /[A-Z]/.test(v), message: "uppercase" },
  { rule: (v) => /[a-z]/.test(v), message: "lowercase" },
  { rule: (v) => /[0-9]/.test(v), message: "number" },
  {
    rule: (v) => /[^A-Za-z0-9]/.test(v),
    message: "special",
  },
];

const StatsContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  background: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
}));

const StyledStatCard = styled(Paper)(({ theme, color = "primary" }) => ({
  padding: theme.spacing(3),
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(135deg, ${theme.palette[color].light} 0%, ${theme.palette[color].main} 100%)`
      : `linear-gradient(135deg, ${theme.palette[color].dark} 0%, ${theme.palette[color].main} 100%)`,
  borderRadius: theme.shape.borderRadius * 1.5,
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%)",
    zIndex: 1,
  },
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 12px 24px ${theme.palette[color].main}20`,
  },
}));

const StatCard = memo(StyledStatCard);

const StatIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  background: "rgba(255,255,255,0.15)",
  backdropFilter: "blur(8px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: theme.spacing(2),
  position: "relative",
  zIndex: 2,
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: "2.5rem",
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  color: "#ffffff",
  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
  position: "relative",
  zIndex: 2,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 500,
  color: "rgba(255,255,255,0.95)",
  letterSpacing: "0.5px",
  position: "relative",
  zIndex: 2,
}));

const StatTrend = styled(Box)(({ theme, positive }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.shape.borderRadius,
  background: positive ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.15)",
  backdropFilter: "blur(4px)",
  color: "#ffffff",
  fontSize: "0.875rem",
  fontWeight: 600,
  position: "relative",
  zIndex: 2,
  width: "fit-content",
}));

// Lazy load components that aren't immediately needed
const PDFReader = lazy(() => import("../../../components/readers/PDFReader"));
const AudioPlayer = lazy(
  () => import("../../../components/readers/AudioPlayer"),
);
const AddBookForm = lazy(() => import("./AddBookForm"));

// Add these styled components after your existing styled components
const BookTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  display: "-webkit-box",
  "-webkit-line-clamp": 2,
  "-webkit-box-orient": "vertical",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const BookMeta = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
  alignItems: "center",
  marginTop: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(1),
  },
}));

const BookCover = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 120,
  height: 160,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    width: 90,
    height: 120,
  },
}));

const BookInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(3),
}));

const CollectionIcon = styled(Box)(({ theme, format }) => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor:
    format === "PDF" ? theme.palette.error.light : theme.palette.primary.light,
  color:
    format === "PDF" ? theme.palette.error.main : theme.palette.primary.main,
}));

const CollectionAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: "12px !important",
  marginBottom: theme.spacing(2),
  boxShadow: "none",
  border: `1px solid ${theme.palette.divider}`,
  "&:before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  "& .MuiAccordionDetails-root": {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
}));

// Add these styled components at the top with other styled components
const LibraryCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  overflow: "hidden",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  },
  marginBottom: theme.spacing(2),
}));

const BookImage = styled("div")(({ theme }) => ({
  width: "120px",
  height: "160px",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  position: "relative",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  [theme.breakpoints.down("sm")]: {
    width: "90px",
    height: "120px",
  },
}));

const BookMetadata = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

// Add new styled components for subscriptions
const SubscriptionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const statusStyles = {
    ACTIVE: {
      background: theme.palette.success.main,
      color: "#ffffff",
      "&:hover": {
        background: theme.palette.success.dark,
      },
    },
    PENDING: {
      background: theme.palette.warning.main,
      color: "#ffffff",
      "&:hover": {
        background: theme.palette.warning.dark,
      },
    },
    EXPIRED: {
      background: theme.palette.error.main,
      color: "#ffffff",
      "&:hover": {
        background: theme.palette.error.dark,
      },
    },
    CANCELLED: {
      background: theme.palette.grey[500],
      color: "#ffffff",
      "&:hover": {
        background: theme.palette.grey[600],
      },
    },
  };

  const baseStyles = {
    fontWeight: 600,
    padding: "4px 12px",
    height: 32,
    "& .MuiChip-icon": {
      color: "inherit",
      marginLeft: "4px",
      marginRight: "-4px",
    },
    "& .MuiChip-label": {
      padding: "0 8px",
      fontSize: "0.875rem",
    },
    transition: theme.transitions.create(["background-color", "box-shadow"], {
      duration: theme.transitions.duration.short,
    }),
    boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
    "&:hover": {
      boxShadow: "0 4px 8px rgba(0,0,0,0.12)",
    },
  };

  return {
    ...baseStyles,
    ...(statusStyles[status] || statusStyles.CANCELLED),
  };
});

const SubscriptionDetail = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

const Profile = memo(() => {
  const [currentTab, setCurrentTab] = useState(0);
  const [sortOrder, setSortOrder] = useState("recent");
  const [isEditing, setIsEditing] = useState(false);
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  const theme = useTheme();

  // Fetch user data
  const { data: userData, isLoading: userLoading } = useUser();

  const [personalInfo, setPersonalInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Update personal info when userData is loaded
  React.useEffect(() => {
    if (userData) {
      setPersonalInfo({
        firstName: userData.first_name || "",
        lastName: userData.last_name || "",
        email: userData.email || "",
      });
    }
  }, [userData]);

  // Profile update mutation
  const {
    handleAdd: handleProfileUpdate,
    loading: profileUpdateLoading,
    SnackbarMessage: ProfileUpdateSnackbar,
  } = useUpdate(
    "profile",
    localStorage.getItem("token"),
    t('auth.profile.snackbar.profileUpdated'),
    t('auth.profile.snackbar.profileUpdateFailed'),
  );

  // Password update mutation
  const {
    handleAdd: handlePasswordEdit,
    loading: passwordUpdateLoading,
    SnackbarMessage: PasswordUpdateSnackbar,
  } = useUpdate(
    "change-password",
    localStorage.getItem("token"),
    t('auth.profile.snackbar.passwordUpdated'),
    t('auth.profile.snackbar.passwordUpdateFailed'),
  );

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [passwordErrors, setPasswordErrors] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");

  // Add password visibility state
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // Add these state declarations at the beginning of the Profile component
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPurchases, setFilteredPurchases] = useState([]);

  // Add this state inside Profile component
  const [isAuthor, setIsAuthor] = useState(false);

  // Add authored books data fetch
  const {
    data: authoredBooks,
    isLoading: authoredBooksLoading,
    refetch: refetchAuthoredBooks,
  } = useAuthoredBooks();

  // Add these new states at the beginning of the Profile component
  const [authorSearchQuery, setAuthorSearchQuery] = useState("");
  const [authorSortOrder, setAuthorSortOrder] = useState("recent");
  const [filteredAuthorBooks, setFilteredAuthorBooks] = useState([]);

  // Add this state near the other state declarations at the beginning of the Profile component
  const [loadingBookId, setLoadingBookId] = useState(null);

  // Replace the subscription history state and loading state
  const {
    data: subscriptionHistory,
    isLoading: loadingSubscriptions,
    SnackbarMessage: SubscriptionSnackbar,
  } = useSubscriptions();

  // Add this state for magazine sorting and search
  const [magazineSortOrder, setMagazineSortOrder] = useState("recent");
  const [magazineSearchQuery, setMagazineSearchQuery] = useState("");

  // Add magazines data fetch
  const { data: magazinesData, isLoading: magazinesLoading } =
    useMagazinePurchases({
      orderBy:
        magazineSortOrder === "recent"
          ? "-publication_date"
          : magazineSortOrder === "oldest"
            ? "publication_date"
            : magazineSortOrder === "title"
              ? "title"
              : "-publication_date",
    });

  // Filter and sort magazines
  const filteredMagazines = useMemo(() => {
    if (!magazinesData) return [];

    let filtered = [...magazinesData];
    if (magazineSearchQuery) {
      const query = magazineSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (magazine) =>
          magazine.title?.toLowerCase().includes(query) ||
          magazine.publisher?.toLowerCase().includes(query) ||
          magazine.subtitle?.toLowerCase().includes(query) ||
          // Safely handle authors array
          (Array.isArray(magazine.authors) &&
            magazine.authors.some((author) =>
              (author.first_name + " " + author.last_name)
                ?.toLowerCase()
                .includes(query),
            )),
      );
    }

    return filtered;
  }, [magazinesData, magazineSearchQuery]);

  // Add this effect to check if user is an author
  useEffect(() => {
    if (userData && userData.is_author) {
      setIsAuthor(true);
    }
  }, [userData]);

  // Add this effect to handle author books filtering and sorting
  useEffect(() => {
    if (!authoredBooks) return;

    let filtered = [...authoredBooks];

    // Apply search filter
    if (authorSearchQuery) {
      const query = authorSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (book) =>
          book.title.toLowerCase().includes(query) ||
          book.isbn13.includes(query) ||
          book.publisher.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (authorSortOrder) {
        case "recent":
          return new Date(b.publication_date) - new Date(a.publication_date);
        case "oldest":
          return new Date(a.publication_date) - new Date(b.publication_date);
        case "title":
          return a.title.localeCompare(b.title);
        case "price-high":
          return b.price - a.price;
        case "price-low":
          return a.price - b.price;
        default:
          return 0;
      }
    });

    setFilteredAuthorBooks(filtered);
  }, [authoredBooks, authorSearchQuery, authorSortOrder]);

  // Validate password as user types
  const validatePassword = (password) => {
    const errors = PASSWORD_REQUIREMENTS.filter(
      (req) => !req.rule(password),
    ).map((req) => {
      const requirementKey = (() => {
        if (req.message.includes("characters long")) return 'length';
        if (req.message.includes("uppercase")) return 'uppercase';
        if (req.message.includes("lowercase")) return 'lowercase';
        if (req.message.includes("number")) return 'number';
        if (req.message.includes("special")) return 'special';
        return '';
      })();
      return t(`auth.profile.password.requirements.${requirementKey}`);
    });
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleUpdate = () => {
    handleProfileUpdate({
      first_name: personalInfo.firstName,
      last_name: personalInfo.lastName,
      email: personalInfo.email,
    });
    setIsEditing(false);
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    switch (name) {
      case "currentPassword":
        setCurrentPassword(value);
        break;
      case "newPassword":
        setNewPassword(value);
        validatePassword(value);
        break;
      case "confirmPassword":
        setConfirmPassword(value);
        break;
      default:
        break;
    }
  };

  // Add toggle handler
  const handleTogglePassword = (field) => () => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Update the password update handler
  const handlePasswordUpdate = (event) => {
    event.preventDefault();

    if (!validatePassword(newPassword)) {
      return;
    }

    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: t('auth.profile.snackbar.passwordMismatch'),
        severity: "error",
      });
      return;
    }

    handlePasswordEdit({
      current_password: currentPassword,
      new_password: newPassword,
    })
      .then(() => {
        // Clear form only on success
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordErrors([]);
      })
      .catch((error) => {
        // Error handling is done by useUpdate hook
        console.log("Password update failed:", error);
      });
  };

  const handleChange = (field) => (event) => {
    setPersonalInfo((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  // Add purchases data fetch
  const { data: purchasesData, isLoading: purchasesLoading } =
    useUserPurchases();

  // Memoize the filtered data calculations
  const filteredData = useMemo(() => {
    if (!purchasesData) return [];

    let filtered = [...purchasesData];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (purchase) =>
          purchase.book.title.toLowerCase().includes(query) ||
          purchase.book.authors_display.some((author) =>
            author.toLowerCase().includes(query),
          ),
      );
    }

    return filtered.sort((a, b) => {
      switch (sortOrder) {
        case "recent":
          return new Date(b.purchase_date) - new Date(a.purchase_date);
        case "oldest":
          return new Date(a.purchase_date) - new Date(b.purchase_date);
        case "title":
          return a.book.title.localeCompare(b.book.title);
        default:
          return 0;
      }
    });
  }, [purchasesData, searchQuery, sortOrder]);

  // Throttle search handlers
  const handleSearch = throttle((event) => {
    setSearchQuery(event.target.value);
  }, 300);

  // Add read book handler
  const [readerOpen, setReaderOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  const handleReadBook = (book, collectionId) => {
    setLoadingBookId(`${book.content_id}-${collectionId}`);
    setSelectedBook({ ...book, selectedCollectionId: collectionId });
    setReaderOpen(true);
  };

  // Add these states in the Profile component
  const [libraryPage, setLibraryPage] = useState(1);
  const [authorPage, setAuthorPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Add pagination handlers
  const handleLibraryPageChange = (event, value) => {
    setLibraryPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleAuthorPageChange = (event, value) => {
    setAuthorPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Memoize the author search handler
  const handleAuthorSearch = throttle((event) => {
    setAuthorSearchQuery(event.target.value);
    setAuthorPage(1);
  }, 300);

  // Add this function to get status icon
  const getStatusIcon = (status) => {
    const iconProps = {
      sx: {
        fontSize: 20,
        color: "inherit",
        transition: "transform 0.2s ease",
        transform: "translateY(-1px)", // Subtle alignment adjustment
      },
    };

    switch (status) {
      case "ACTIVE":
        return <CheckCircleIcon {...iconProps} />;
      case "PENDING":
        return (
          <PendingIcon
            {...iconProps}
            sx={{
              ...iconProps.sx,
              animation: "spin 2s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        );
      case "EXPIRED":
        return <AccessTimeIcon {...iconProps} />;
      case "CANCELLED":
        return <CancelIcon {...iconProps} />;
      default:
        return <AutorenewIcon {...iconProps} />;
    }
  };

  // Update the author dashboard section
  const [isAddBookOpen, setIsAddBookOpen] = useState(false);

  const authorDashboard = (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('auth.profile.authorDashboard.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsAddBookOpen(true)}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
          }}
        >
          {t('auth.profile.authorDashboard.addBook')}
        </Button>
      </Box>

      <StatsContainer>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <StatCard color="primary">
              <StatIcon>
                <MenuBookIcon sx={{ color: "#ffffff", fontSize: 28 }} />
              </StatIcon>
              <StatValue>{filteredAuthorBooks?.length || 0}</StatValue>
              <StatLabel>{t('auth.profile.authorDashboard.stats.totalBooks')}</StatLabel>
              <StatTrend positive>
                <TrendingUpIcon fontSize="small" />
                <span>{t('auth.profile.authorDashboard.stats.moreBooks', { percent: 12 })}</span>
              </StatTrend>
            </StatCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard color="success">
              <StatIcon>
                <LocalAtmIcon sx={{ color: "#ffffff", fontSize: 28 }} />
              </StatIcon>
              <StatValue>
                {filteredAuthorBooks?.reduce(
                  (total, book) => total + book.total_purchases,
                  0,
                ) || 0}
              </StatValue>
              <StatLabel>{t('auth.profile.authorDashboard.stats.totalSales')}</StatLabel>
              <StatTrend positive>
                <TrendingUpIcon fontSize="small" />
                <span>{t('auth.profile.authorDashboard.stats.salesIncrease', { percent: 8 })}</span>
              </StatTrend>
            </StatCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard color="warning">
              <StatIcon>
                <PublishIcon sx={{ color: "#ffffff", fontSize: 28 }} />
              </StatIcon>
              <StatValue>
                {filteredAuthorBooks?.filter((book) => book.is_published)
                  .length || 0}
              </StatValue>
              <StatLabel>{t('auth.profile.authorDashboard.stats.publishedBooks')}</StatLabel>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  background: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(4px)",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  color: "#ffffff",
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  width: "fit-content",
                  zIndex: 2,
                  position: "relative",
                }}
              >
                <CircularProgress
                  variant="determinate"
                  value={
                    filteredAuthorBooks?.length
                      ? (filteredAuthorBooks.filter((b) => b.is_published)
                          .length /
                          filteredAuthorBooks.length) *
                        100
                      : 0
                  }
                  size={24}
                  thickness={4}
                  sx={{
                    color: "#ffffff",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    },
                  }}
                />
                <span>
                  {t('auth.profile.authorDashboard.stats.publishedPercent', {
                    percent: filteredAuthorBooks?.length
                      ? Math.round(
                          (filteredAuthorBooks.filter((b) => b.is_published)
                            .length /
                            filteredAuthorBooks.length) *
                            100,
                        )
                      : 0
                  })}
                </span>
              </Box>
            </StatCard>
          </Grid>
        </Grid>
      </StatsContainer>

      <Suspense fallback={<CircularProgress />}>
        <AddBookForm
          open={isAddBookOpen}
          onClose={() => setIsAddBookOpen(false)}
          onSuccess={() => {
            setIsAddBookOpen(false);
            // Optionally refresh the books list
            refetchAuthoredBooks && refetchAuthoredBooks();
          }}
        />
      </Suspense>

      <SearchContainer>
        <TextField
          placeholder={t('auth.profile.authorDashboard.search.placeholder')}
          variant="outlined"
          fullWidth
          value={authorSearchQuery}
          onChange={handleAuthorSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Select
          value={authorSortOrder}
          onChange={(e) => {
            setAuthorSortOrder(e.target.value);
            setAuthorPage(1); // Reset to first page when sorting
          }}
          variant="outlined"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="recent">{t('auth.profile.authorDashboard.sort.recent')}</MenuItem>
          <MenuItem value="oldest">{t('auth.profile.authorDashboard.sort.oldest')}</MenuItem>
          <MenuItem value="title">{t('auth.profile.authorDashboard.sort.title')}</MenuItem>
          <MenuItem value="price-high">{t('auth.profile.authorDashboard.sort.priceHigh')}</MenuItem>
          <MenuItem value="price-low">{t('auth.profile.authorDashboard.sort.priceLow')}</MenuItem>
        </Select>
      </SearchContainer>

      {authoredBooksLoading ? (
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('auth.profile.authorDashboard.table.cover')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.title')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.format')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.publicationDate')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.price')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.totalSales')}</TableCell>
                <TableCell>{t('auth.profile.authorDashboard.table.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <BookTableSkeleton rows={5} />
            </TableBody>
          </Table>
        </TableContainer>
      ) : filteredAuthorBooks?.length > 0 ? (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('auth.profile.authorDashboard.table.cover')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.title')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.format')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.publicationDate')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.price')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.totalSales')}</TableCell>
                  <TableCell>{t('auth.profile.authorDashboard.table.status')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAuthorBooks
                  .slice(
                    (authorPage - 1) * ITEMS_PER_PAGE,
                    authorPage * ITEMS_PER_PAGE,
                  )
                  .map((book) => (
                    <TableRow key={book.id}>
                      <TableCell>
                        <Box
                          component="img"
                          src={book.cover_image}
                          alt={book.title}
                          sx={{
                            width: 60,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 1,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {book.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('auth.profile.authorDashboard.table.isbn')} {book.isbn13}
                        </Typography>
                      </TableCell>
                      <TableCell>{book.format}</TableCell>
                      <TableCell>
                        {formatDate(
                          new Date(book.publication_date),
                          "MMM dd, yyyy",
                        )}
                      </TableCell>
                      <TableCell>{book.price > 0 ? `$${book.price}` : t('auth.profile.authorDashboard.table.free')}</TableCell>
                      <TableCell>
                        <Chip
                          label={book.total_purchases}
                          color="primary"
                          size="small"
                          sx={{
                            fontWeight: "bold",
                            minWidth: 60,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            backgroundColor: book.is_published
                              ? "success.light"
                              : "warning.light",
                            color: book.is_published
                              ? "success.dark"
                              : "warning.dark",
                            py: 0.5,
                            px: 1.5,
                            borderRadius: 1,
                            display: "inline-block",
                          }}
                        >
                          {book.is_published 
                            ? t('auth.profile.authorDashboard.table.published') 
                            : t('auth.profile.authorDashboard.table.draft')}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            <Pagination
              count={Math.ceil(filteredAuthorBooks.length / ITEMS_PER_PAGE)}
              page={authorPage}
              onChange={handleAuthorPageChange}
              color="primary"
              size="large"
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            backgroundColor: "background.paper",
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {authorSearchQuery
              ? t('auth.profile.authorDashboard.empty.searchEmpty')
              : t('auth.profile.authorDashboard.empty.noBooks')}
          </Typography>
        </Box>
      )}
    </>
  );

  // Add this at the beginning of the Profile component
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, []);

  // Update the effect to reset loading state when dialog is closed
  useEffect(() => {
    if (!readerOpen) {
      setLoadingBookId(null);
    }
  }, [readerOpen]);

  // Update the subscriptions section component
  const subscriptionsSection = (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          px: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('auth.profile.subscriptions.title')}
        </Typography>
        {subscriptionHistory && subscriptionHistory.length > 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('auth.profile.subscriptions.count', { count: subscriptionHistory.length })}
          </Typography>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        {loadingSubscriptions ? (
          // Subscription loading skeleton
          [...Array(3)].map((_, index) => (
            <SubscriptionCard key={index}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Skeleton variant="text" width="60%" height={32} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="text" width="40%" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Skeleton variant="text" width="30%" />
                </Grid>
              </Grid>
            </SubscriptionCard>
          ))
        ) : subscriptionHistory && subscriptionHistory.length > 0 ? (
          subscriptionHistory.map((subscription) => (
            <SubscriptionCard key={subscription.id}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {subscription.plan_name}
                    </Typography>
                    <StatusChip
                      icon={getStatusIcon(subscription.subscription_status)}
                      label={t(`auth.profile.subscriptions.status.${subscription.subscription_status.toLowerCase()}`)}
                      status={subscription.subscription_status}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {subscription.plan_description}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SubscriptionDetail>
                    <CalendarIcon />
                    <Typography variant="body2">
                      {t('auth.profile.subscriptions.details.startDate')}{" "}
                      {formatDate(
                        new Date(subscription.start_date),
                        "MMM dd, yyyy",
                      )}
                    </Typography>
                  </SubscriptionDetail>
                  <SubscriptionDetail>
                    <AccessTimeIcon />
                    <Typography variant="body2">
                      {t('auth.profile.subscriptions.details.endDate')}{" "}
                      {formatDate(
                        new Date(subscription.end_date),
                        "MMM dd, yyyy",
                      )}
                    </Typography>
                  </SubscriptionDetail>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <SubscriptionDetail>
                    <PaymentIcon />
                    <Typography variant="body2">
                      {t('auth.profile.subscriptions.details.price')} {subscription.formatted_price}
                    </Typography>
                  </SubscriptionDetail>
                  {subscription.subscription_status === "ACTIVE" &&
                    subscription.days_remaining && (
                      <SubscriptionDetail>
                        <AutorenewIcon />
                        <Typography variant="body2">
                          {t('auth.profile.subscriptions.details.daysRemaining', { count: subscription.days_remaining })}
                        </Typography>
                      </SubscriptionDetail>
                    )}
                </Grid>

                {subscription.subscription_status === "ACTIVE" &&
                  subscription.days_remaining && (
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          mt: 2,
                          pt: 2,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <LinearProgress
                          variant="determinate"
                          value={
                            ((new Date(subscription.end_date) -
                              new Date(subscription.start_date) -
                              subscription.days_remaining *
                                24 *
                                60 *
                                60 *
                                1000) /
                              (new Date(subscription.end_date) -
                                new Date(subscription.start_date))) *
                            100
                          }
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: "action.hover",
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 4,
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  )}
              </Grid>
            </SubscriptionCard>
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "background.paper",
              borderRadius: 2,
              px: 2,
            }}
          >
            <AutorenewIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {t('auth.profile.subscriptions.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('auth.profile.subscriptions.empty.description')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Update the magazines section component
  const magazinesSection = (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          px: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('auth.profile.magazines.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.profile.magazines.count', { count: filteredMagazines?.length || 0 })}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        <SearchContainer>
          <TextField
            placeholder={t('auth.profile.magazines.search.placeholder')}
            variant="outlined"
            fullWidth
            onChange={(e) => setMagazineSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Select
            value={magazineSortOrder}
            onChange={(e) => setMagazineSortOrder(e.target.value)}
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <MenuItem value="recent">{t('auth.profile.magazines.sort.recent')}</MenuItem>
            <MenuItem value="oldest">{t('auth.profile.magazines.sort.oldest')}</MenuItem>
            <MenuItem value="title">{t('auth.profile.magazines.sort.title')}</MenuItem>
          </Select>
        </SearchContainer>

        {magazinesLoading ? (
          <BookTableSkeleton rows={5} />
        ) : filteredMagazines?.length > 0 ? (
          filteredMagazines.map((magazine) => (
            <LibraryCard key={magazine.id}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3} alignItems="flex-start">
                  <Grid item>
                    <BookImage>
                      <img
                        src={magazine.cover_image}
                        alt={magazine.title}
                        loading="lazy"
                      />
                    </BookImage>
                  </Grid>
                  <Grid item xs>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "1.1rem", sm: "1.25rem" },
                          mb: 0.5,
                        }}
                      >
                        {magazine.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {magazine.subtitle}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                        <BookMetadata>
                          <CalendarIcon />
                          <Typography variant="body2">
                            {formatDate(
                              new Date(magazine.publication_date),
                              "MMM dd, yyyy",
                            )}
                          </Typography>
                        </BookMetadata>
                        <BookMetadata>
                          <Description />
                          <Typography variant="body2">
                            {t('auth.profile.magazines.issue', { number: magazine.issue_number })}
                          </Typography>
                        </BookMetadata>
                      </Box>

                      <Box sx={{ mt: "auto" }}>
                        {magazine.file && (
                          <Button
                            variant="contained"
                            startIcon={<ReadIcon />}
                            onClick={() => handleReadMagazine(magazine)}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              px: 3,
                            }}
                          >
                            {t('auth.profile.magazines.buttons.read')}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </LibraryCard>
          ))
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "background.paper",
              borderRadius: 2,
              px: 2,
            }}
          >
            <MenuBookIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {magazineSearchQuery
                ? t('auth.profile.magazines.empty.searchEmpty')
                : t('auth.profile.magazines.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {magazineSearchQuery
                ? t('auth.profile.magazines.empty.searchTip')
                : t('auth.profile.magazines.empty.description')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Personal Details Section
  const personalDetailsSection = (
    <>
      <Typography variant="h5" sx={{ mt: 4, mb: 3 }}>
        {t('auth.profile.personalDetails.title')}
      </Typography>

      {userLoading ? (
        <ProfileSkeleton />
      ) : !isEditing ? (
        <InfoSectionWrapper>
          <InfoSection>
            <EditIconWrapper className="edit-icon" onClick={handleEdit}>
              <EditIcon fontSize="small" />
            </EditIconWrapper>
            <InfoGrid container spacing={4}>
              <Grid item xs={12} sm={4}>
                <InfoLabel>{t('auth.profile.personalDetails.fields.firstName')}</InfoLabel>
                <InfoValue>{personalInfo.firstName}</InfoValue>
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoLabel>{t('auth.profile.personalDetails.fields.lastName')}</InfoLabel>
                <InfoValue>{personalInfo.lastName}</InfoValue>
              </Grid>
              <Grid item xs={12} sm={4}>
                <InfoLabel>{t('auth.profile.personalDetails.fields.email')}</InfoLabel>
                <InfoValue>{personalInfo.email}</InfoValue>
              </Grid>
            </InfoGrid>
          </InfoSection>
        </InfoSectionWrapper>
      ) : (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('auth.profile.personalDetails.fields.firstName')}
                value={personalInfo.firstName}
                onChange={handleChange("firstName")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('auth.profile.personalDetails.fields.lastName')}
                value={personalInfo.lastName}
                onChange={handleChange("lastName")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={t('auth.profile.personalDetails.fields.email')}
                value={personalInfo.email}
                onChange={handleChange("email")}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", gap: 2, justifyContent: isRtl ? "flex-start" : "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{ textTransform: "uppercase" }}
                >
                  {t('auth.profile.personalDetails.buttons.cancel')}
                </Button>
                {updateButton}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('auth.profile.password.title')}
      </Typography>
      <form onSubmit={handlePasswordUpdate}>
        <Grid container spacing={3} sx={{ maxWidth: 800 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type={showPasswords.current ? "text" : "password"}
              name="currentPassword"
              label={t('auth.profile.password.fields.currentPassword')}
              variant="outlined"
              required
              value={currentPassword}
              onChange={handlePasswordChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword("current")}
                      edge="end"
                      size="large"
                    >
                      {showPasswords.current ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type={showPasswords.new ? "text" : "password"}
              name="newPassword"
              label={t('auth.profile.password.fields.newPassword')}
              variant="outlined"
              required
              value={newPassword}
              onChange={handlePasswordChange}
              error={passwordErrors.length > 0}
              helperText={
                passwordErrors.length > 0 ? t('auth.profile.password.errors.requirements') : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword("new")}
                      edge="end"
                      size="large"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ mt: 1 }}>
              {PASSWORD_REQUIREMENTS.map((req, index) => {
                const requirementKey = (() => {
                  if (req.message.includes("characters long")) return 'length';
                  if (req.message.includes("uppercase")) return 'uppercase';
                  if (req.message.includes("lowercase")) return 'lowercase';
                  if (req.message.includes("number")) return 'number';
                  if (req.message.includes("special")) return 'special';
                  return '';
                })();
                
                return (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{
                      display: "block",
                      color: req.rule(newPassword)
                        ? "success.main"
                        : "text.secondary",
                      "&::before": {
                        content: '"✓ "',
                        color: req.rule(newPassword)
                          ? "success.main"
                          : "text.disabled",
                      },
                    }}
                  >
                    {t(`auth.profile.password.requirements.${requirementKey}`)}
                  </Typography>
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type={showPasswords.confirm ? "text" : "password"}
              name="confirmPassword"
              label={t('auth.profile.password.fields.confirmPassword')}
              variant="outlined"
              required
              value={confirmPassword}
              onChange={handlePasswordChange}
              error={confirmPassword && newPassword !== confirmPassword}
              helperText={
                confirmPassword && newPassword !== confirmPassword
                  ? t('auth.profile.password.errors.mismatch')
                  : ""
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePassword("confirm")}
                      edge="end"
                      size="large"
                    >
                      {showPasswords.confirm ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: isRtl ? "flex-start" : "flex-end" }}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  passwordUpdateLoading ||
                  passwordErrors.length > 0 ||
                  !currentPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  newPassword !== confirmPassword
                }
                sx={{ textTransform: "uppercase", px: 4 }}
              >
                {passwordUpdateLoading 
                  ? t('auth.profile.password.buttons.updating') 
                  : t('auth.profile.password.buttons.update')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </>
  );

  // Update the ordersSection with proper component usage
  const ordersSection = (
    <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          px: { xs: 2, sm: 0 },
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('auth.profile.library.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth.profile.library.booksCount', { count: filteredData?.length || 0 })}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 0 } }}>
        <SearchContainer>
          <TextField
            placeholder={t('auth.profile.library.search.placeholder')}
            variant="outlined"
            fullWidth
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            variant="outlined"
            sx={{ minWidth: { xs: "100%", sm: 200 } }}
          >
            <MenuItem value="recent">{t('auth.profile.library.sort.recent')}</MenuItem>
            <MenuItem value="oldest">{t('auth.profile.library.sort.oldest')}</MenuItem>
            <MenuItem value="title">{t('auth.profile.library.sort.title')}</MenuItem>
          </Select>
        </SearchContainer>

        {purchasesLoading ? (
          <BookTableSkeleton rows={5} />
        ) : filteredData?.length > 0 ? (
          <>
            {filteredData
              .slice(
                (libraryPage - 1) * ITEMS_PER_PAGE,
                libraryPage * ITEMS_PER_PAGE,
              )
              .map((purchase) => (
                <LibraryCard key={purchase.id}>
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3} alignItems="flex-start">
                      <Grid item>
                        <BookImage>
                          <img
                            src={purchase.cover_image}
                            alt={purchase.title}
                            loading="lazy"
                          />
                        </BookImage>
                      </Grid>
                      <Grid item xs>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "1.1rem", sm: "1.25rem" },
                              mb: 0.5,
                            }}
                          >
                            {purchase.title}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            {t('auth.profile.library.bookInfo.by')} {purchase.authors_display?.join(", ")}
                          </Typography>

                          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
                            <BookMetadata>
                              <CalendarIcon />
                              <Typography variant="body2">
                                {formatDate(
                                  new Date(purchase.completed_at),
                                  "MMM dd, yyyy",
                                )}
                              </Typography>
                            </BookMetadata>
                            <BookMetadata>
                              {purchase.is_pdf ? (
                                <PdfIcon color="error" />
                              ) : (
                                <AudioIcon color="primary" />
                              )}
                              <Typography variant="body2">
                                {purchase.format}
                              </Typography>
                            </BookMetadata>
                          </Box>

                          <Box sx={{ mt: "auto" }}>
                            <Typography
                              variant="subtitle2"
                              sx={{ mb: 1, color: "text.secondary" }}
                            >
                              {t('auth.profile.library.bookInfo.availableFiles')}
                            </Typography>
                            <Box
                              sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                            >
                              {purchase.collections.map((collection) => (
                                <Button
                                  key={collection.id}
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    handleReadBook(purchase, collection.id)
                                  }
                                  disabled={
                                    loadingBookId ===
                                    `${purchase.content_id}-${collection.id}`
                                  }
                                  startIcon={
                                    loadingBookId ===
                                    `${purchase.content_id}-${collection.id}` ? (
                                      <CircularProgress size={16} />
                                    ) : purchase.is_pdf ? (
                                      <PdfIcon />
                                    ) : (
                                      <AudioIcon />
                                    )
                                  }
                                  sx={{
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    px: 2,
                                  }}
                                >
                                  {loadingBookId ===
                                  `${purchase.content_id}-${collection.id}`
                                    ? t('auth.profile.library.bookInfo.loading')
                                    : `${purchase.is_pdf 
                                        ? t('auth.profile.library.bookInfo.read') 
                                        : t('auth.profile.library.bookInfo.play')} ${collection.filename}`}
                                </Button>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </LibraryCard>
              ))}

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                mb: 2,
              }}
            >
              <Pagination
                count={Math.ceil(filteredData.length / ITEMS_PER_PAGE)}
                page={libraryPage}
                onChange={handleLibraryPageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "background.paper",
              borderRadius: 2,
              px: 2,
            }}
          >
            <MenuBookIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              {searchQuery
                ? t('auth.profile.library.empty.searchEmpty')
                : t('auth.profile.library.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? t('auth.profile.library.empty.searchTip')
                : t('auth.profile.library.empty.description')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Update the buttons to show loading state
  const updateButton = (
    <Button
      variant="contained"
      onClick={handleUpdate}
      disabled={profileUpdateLoading}
      sx={{ textTransform: "uppercase" }}
    >
      {profileUpdateLoading 
        ? t('auth.profile.personalDetails.buttons.updating') 
        : t('auth.profile.personalDetails.buttons.update')}
    </Button>
  );

  // Define tabs after all sections are defined
  const tabs = [
    { label: t('auth.profile.tabs.personalDetails'), content: personalDetailsSection },
    { label: t('auth.profile.tabs.myLibrary'), content: ordersSection },
    { label: t('auth.profile.tabs.myMagazines'), content: magazinesSection },
    { label: t('auth.profile.tabs.subscriptions'), content: subscriptionsSection },
  ];

  if (isAuthor) {
    tabs.push({ label: t('auth.profile.tabs.authorDashboard'), content: authorDashboard });
  }

  // Add state for magazine reader
  const [magazineReaderOpen, setMagazineReaderOpen] = useState(false);
  const [selectedMagazine, setSelectedMagazine] = useState(null);

  // Add magazine reader handler
  const handleReadMagazine = (magazine) => {
    setSelectedMagazine(magazine);
    setMagazineReaderOpen(true);
  };

  return (
    <Box
      sx={{ py: 4, backgroundColor: "background.default", minHeight: "60vh" }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
          {t('auth.profile.title')}
        </Typography>

        <StyledTabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </StyledTabs>

        {tabs[currentTab]?.content}
      </Container>
      {ProfileUpdateSnackbar}
      {PasswordUpdateSnackbar}
      {SubscriptionSnackbar}
      <SnackbarMessage
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
      <Suspense fallback={null}>
        <Dialog
          fullScreen
          open={readerOpen}
          onClose={() => setReaderOpen(false)}
        >
          {selectedBook?.is_pdf ? (
            <PDFReader
              url={`${process.env.REACT_APP_API_URL}/books/${selectedBook.content_id}/read/?collection_id=${selectedBook.selectedCollectionId}`}
              onClose={() => setReaderOpen(false)}
              onLoad={() => setLoadingBookId(null)}
            />
          ) : selectedBook?.is_audio ? (
            <AudioPlayer
              url={`${process.env.REACT_APP_API_URL}/books/${selectedBook.content_id}/read/?collection_id=${selectedBook.selectedCollectionId}`}
              title={selectedBook.title}
              author={`${t('auth.profile.library.bookInfo.by')} ${selectedBook.authors_display?.join(", ")}`}
              onClose={() => setReaderOpen(false)}
              token={localStorage.getItem("token")}
              onLoad={() => setLoadingBookId(null)}
            />
          ) : null}
        </Dialog>
      </Suspense>
      <Suspense fallback={null}>
        <Dialog
          fullScreen
          open={magazineReaderOpen}
          onClose={() => setMagazineReaderOpen(false)}
        >
          {selectedMagazine && selectedMagazine.file && (
            <PDFReader
              url={`${process.env.REACT_APP_API_URL}/magazines/${selectedMagazine.content_id}/read/`}
              onClose={() => setMagazineReaderOpen(false)}
            />
          )}
        </Dialog>
      </Suspense>
    </Box>
  );
});

export default Profile;
